import { Injectable } from '@angular/core';
import { GuildController__factory } from '@data/abi';
import { TransactionDataModel } from '@models/transaction-data.model';
import { ErrorService } from '@services/error.service';
import { FeesExtension, ON_CHAIN_CALL_DELAY, ON_CHAIN_CALL_RETRY } from '@services/onchain/FeesExtension';
import { RelayService } from '@services/onchain/relay.service';
import { ProviderService } from '@services/provider.service';
import { GET_CORE_ADDRESSES } from '@shared/constants/addresses/addresses.constant';
import { GuildRequestStatus, GuildsParams } from '@shared/constants/game.constant';
import { adjustGasLimit } from '@shared/utils';
import { NGXLogger } from 'ngx-logger';
import { Observable, catchError, concatMap, from, map, retry } from 'rxjs';

export type GuildDataType = [string, string, string, bigint, bigint, bigint] & {
  guildName: string;
  urlLogo: string;
  owner: string;
  guildLevel: bigint;
  pvpCounter: bigint;
  toHelperRatio: bigint;
};

export type GuildRequestType = [bigint, string, bigint, string] & {
  status: bigint;
  user: string;
  guildId: bigint;
  userMessage: string;
  reqId?: bigint;
};

export type GuildUserPvpPoints = [bigint, bigint] & {
  capacityPvpPoints: bigint;
  spentPvpPoints: bigint;
};

@Injectable({
  providedIn: 'root',
})
export class GuildsControllerService extends FeesExtension {
  constructor(
    private providerService: ProviderService,
    private errorService: ErrorService,
    private logger: NGXLogger,
    private relayService: RelayService,
  ) {
    super();
  }

  // --- FACTORIES ---

  private createGuildController(chainId: number) {
    return GuildController__factory.connect(
      GET_CORE_ADDRESSES(chainId).guildController,
      this.providerService.getProviderForRead(),
    );
  }

  // --- VIEWS ---

  getGuildParamValue$(value: GuildsParams, chainId: number) {
    this.logger.trace('getGuildParamValue', value, chainId);
    return from(this.createGuildController(chainId).getGuildParamValue(value)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  memberOf$(account: string, chainId: number) {
    this.logger.trace('memberOf', account, chainId);
    return from(this.createGuildController(chainId).memberOf(account)).pipe(
      map(val => Number(val)),
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getGuildData$(guildId: number, chainId: number): Observable<GuildDataType> {
    this.logger.trace('getGuildData', guildId, chainId);
    return from(this.createGuildController(chainId).getGuildData(guildId)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getGuildRequest$(guildRequest: number, chainId: number): Observable<GuildRequestType> {
    this.logger.trace('getGuildRequest', guildRequest, chainId);
    return from(this.createGuildController(chainId).getGuildRequest(guildRequest)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getUserActiveGuildRequests$(account: string, chainId: number) {
    this.logger.trace('getUserActiveGuildRequests', account, chainId);
    return from(this.createGuildController(chainId).getUserActiveGuildRequests(account)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  guildMembers$(guildId: number, chainId: number) {
    this.logger.trace('guildMembers', guildId, chainId);
    return from(this.createGuildController(chainId).guildMembers(guildId)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getGuildBank$(guildId: number, chainId: number) {
    this.logger.trace('getGuildBank', guildId, chainId);
    return from(this.createGuildController(chainId).getGuildBank(guildId)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getGuildRequestDepositAmount$(guildId: number, chainId: number) {
    this.logger.trace('getGuildRequestDepositAmount', guildId, chainId);
    return from(this.createGuildController(chainId).getGuildRequestDepositAmount(guildId)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getRights$(account: string, chainId: number) {
    this.logger.trace('getRights', account, chainId);
    return from(this.createGuildController(chainId).getRights(account)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  listGuildRequests$(guildId: number, status: GuildRequestStatus, chainId: number) {
    this.logger.trace('listGuildRequests', guildId, status);
    return from(this.createGuildController(chainId).listGuildRequests(guildId, status)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getGuildDescription$(guildId: number, chainId: number) {
    this.logger.trace('getGuildDescription', guildId);
    return from(this.createGuildController(chainId).getGuildDescription(guildId)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  getUserPvpPoints$(guildId: number, account: string, chainId: number): Observable<GuildUserPvpPoints> {
    this.logger.trace('getUserPvpPoints', guildId, account);
    return from(this.createGuildController(chainId).getUserPvpPoints(guildId, account)).pipe(
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(this.errorService.onCatchError),
    );
  }

  // --- CALLS ---

  createGuild$(signer: string, chainId: number, name: string, urlLogo: string, toHelperRatio = 10) {
    return from(this.createGuildController(chainId).createGuild.estimateGas(name, urlLogo, toHelperRatio)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Create guild',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            isNeedUpdateBalances: true,
            txPopulated: this.createGuildController(chainId).createGuild.populateTransaction(
              name,
              urlLogo,
              toHelperRatio,
            ),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  rename$(signer: string, chainId: number, newName: string) {
    return from(this.createGuildController(chainId).rename.estimateGas(newName)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'rename',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            isNeedUpdateBalances: true,
            txPopulated: this.createGuildController(chainId).rename.populateTransaction(newName),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  changeDescription$(signer: string, chainId: number, value: string) {
    return from(this.createGuildController(chainId).changeDescription.estimateGas(value)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'changeDescription',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            isNeedUpdateBalances: false,
            txPopulated: this.createGuildController(chainId).changeDescription.populateTransaction(value),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  changeLogo$(signer: string, chainId: number, logo: string) {
    return from(this.createGuildController(chainId).changeLogo.estimateGas(logo)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'changeLogo',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            isNeedUpdateBalances: true,
            txPopulated: this.createGuildController(chainId).changeLogo.populateTransaction(logo),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  setGuildRequestDepositAmount$(value: bigint, signer: string, chainId: number) {
    return from(this.createGuildController(chainId).setGuildRequestDepositAmount.estimateGas(value)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'setGuildRequestDepositAmount',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).setGuildRequestDepositAmount.populateTransaction(value),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  guildLevelUp$(signer: string, chainId: number) {
    return from(this.createGuildController(chainId).guildLevelUp.estimateGas()).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'guildLevelUp',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            isNeedUpdateBalances: true,
            txPopulated: this.createGuildController(chainId).guildLevelUp.populateTransaction(),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  addGuildRequest$(guildId: number, userMessage: string, signer: string, chainId: number) {
    this.logger.trace('addGuildRequest', guildId, userMessage, signer, chainId);
    return from(this.createGuildController(chainId).addGuildRequest.estimateGas(guildId, userMessage)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'addGuildRequest',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            isNeedUpdateBalances: true,
            txPopulated: this.createGuildController(chainId).addGuildRequest.populateTransaction(guildId, userMessage),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  cancelGuildRequest$(guildRequestId: number, signer: string, chainId: number) {
    this.logger.trace('cancelGuildRequest', guildRequestId, signer, chainId);
    return from(this.createGuildController(chainId).cancelGuildRequest.estimateGas(guildRequestId)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Cancel guild request',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).cancelGuildRequest.populateTransaction(guildRequestId),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  acceptGuildRequest$(guildRequestId: number, right: number, signer: string, chainId: number) {
    this.logger.trace('acceptGuildRequest', guildRequestId, right, signer, chainId);
    return from(this.createGuildController(chainId).acceptGuildRequest.estimateGas(guildRequestId, right)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Accept guild request',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).acceptGuildRequest.populateTransaction(
              guildRequestId,
              right,
            ),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  rejectGuildRequest$(guildRequestId: number, signer: string, chainId: number) {
    this.logger.trace('rejectGuildRequest', guildRequestId, signer, chainId);
    return from(this.createGuildController(chainId).rejectGuildRequest.estimateGas(guildRequestId)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Reject guild request',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).rejectGuildRequest.populateTransaction(guildRequestId),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  setPvpPointsCapacity$(capacity: number, user: string[], signer: string, chainId: number) {
    this.logger.trace('setPvpPointsCapacity', capacity, user, signer, chainId);
    return from(this.createGuildController(chainId).setPvpPointsCapacity.estimateGas(capacity, user)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Set pvp capacity',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).setPvpPointsCapacity.populateTransaction(capacity, user),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  changeRoles$(user: string, maskRights: number, signer: string, chainId: number) {
    this.logger.trace('changeRoles', user, maskRights, signer, chainId);
    return from(this.createGuildController(chainId).changeRoles.estimateGas(user, maskRights)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'changeRoles',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).changeRoles.populateTransaction(user, maskRights),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  removeGuildMember$(user: string, signer: string, chainId: number) {
    this.logger.trace('removeGuildMember', user, signer, chainId);
    return from(this.createGuildController(chainId).removeGuildMember.estimateGas(user)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'removeGuildMember',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).removeGuildMember.populateTransaction(user),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  transferOwnership$(user: string, signer: string, chainId: number) {
    this.logger.trace('transferOwnership', user, signer, chainId);
    return from(this.createGuildController(chainId).transferOwnership.estimateGas(user)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'transferOwnership',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).transferOwnership.populateTransaction(user),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false, // important action should not use delegation
            // relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  transferMulti$(token: string, recipients: string[], amounts: bigint[], signer: string, chainId: number) {
    this.logger.trace('transferMulti', token, recipients, amounts, signer, chainId);
    return from(this.createGuildController(chainId).transferMulti.estimateGas(token, recipients, amounts)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'transferMulti',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).transferMulti.populateTransaction(
              token,
              recipients,
              amounts,
            ),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  transferNftMulti$(to: string, nfts: string[], ids: number[], signer: string, chainId: number) {
    this.logger.trace('transferNftMulti', to, nfts, ids, signer, chainId);
    return from(this.createGuildController(chainId).transferNftMulti.estimateGas(to, nfts, ids)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'transferNftMulti',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).transferNftMulti.populateTransaction(to, nfts, ids),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }

  setToHelperRatio$(value: number, chainId: number) {
    this.logger.trace('setToHelperRatio', value, chainId);
    return from(this.createGuildController(chainId).setToHelperRatio.estimateGas(value)).pipe(
      concatMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas =>
        this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'setToHelperRatio',
            showLoadingScreen: true,
            isNeedUpdateHero: false,
            txPopulated: this.createGuildController(chainId).setToHelperRatio.populateTransaction(value),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: true,
            relayService: this.relayService,
          }),
        ),
      ),
      catchError(this.errorService.onCatchError),
    );
  }
}
